import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Agreement, Product, Profitable, Unchargeable, Unforeseen } from '../schemas';
import { AgreementService, Auth, ProductService } from '../service';
import { ExtraHoursData, ExtraHoursForm, HoursType } from './extra-hours-form';
import { RemoveHoursData, RemoveHoursDialog } from './remove-hours-dialog';

type ExtraHours = Unchargeable | Profitable | Unforeseen;

@Component({
  selector: 'lcm-extra-hours-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTableModule, MatButtonModule, MatTooltipModule, MatIconModule, MatToolbarModule],
  template: `
    <mat-toolbar>
      <h3>{{ title() }}</h3>
      <span class="spacer"></span>
      @if (auth.isAdminOrEditor()) {
        <button [disabled]="pending()" matMiniFab (click)="showForm()">
          <mat-icon>add</mat-icon>
        </button>
      }
    </mat-toolbar>
    @if (source() && source().length) {
      <table mat-table [dataSource]="source()" [trackBy]="trackBy">
        <ng-container matColumnDef="Date">
          <th mat-header-cell mat-sort-header *matHeaderCellDef>Date</th>
          <td mat-cell *matCellDef="let item">{{ item.Month }} {{ item.Year }}</td>
        </ng-container>

        <mat-text-column name="Hours" />
        <mat-text-column name="Notes" />

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let item">
            @if (auth.isAdminOrEditor()) {
              <button [disabled]="pending()" matMiniFab (click)="remove(item)">
                <mat-icon>delete</mat-icon>
              </button>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="columnsToShow()"></tr>
        <tr mat-row *matRowDef="let user; columns: columnsToShow()"></tr>
      </table>
    }
  `,
  styles: ``,
})
export class ExtraHoursTable {
  property = input.required<'UnchargeableHours' | 'ProfitableHours' | 'UnforeseenHours'>();

  owner = input.required<Product | Agreement>();
  service = input.required<ProductService | AgreementService>();

  pending = input(false);

  auth = inject(Auth);
  dialog = inject(MatDialog);
  removed = output();
  added = output();

  columnsToShow = signal(['Date', 'Hours', 'Notes', 'actions']);

  source = computed<ExtraHours[]>(() => {
    const source = this.owner()[this.property()];
    if (Array.isArray(source)) {
      return source;
    }

    console.warn('Source of %s in %s is not an array: %o', this.property(), this.owner().Title, source);
    return [];
  });

  trackBy(idx: number, row: ExtraHours) {
    return row.Id;
  }

  title = computed(() =>
    this.property()
      .split(/(?=[A-Z])/) // Split before each uppercase letter
      .join(' '),
  );

  hoursType = computed(() => {
    const mapping = {
      UnchargeableHours: 'uncharged',
      ProfitableHours: 'profited',
      PurchasedHours: 'purchased',
      UnforeseenHours: 'unforeseen',
    };
    return mapping[this.property()] as HoursType;
  });

  remove(item: ExtraHours) {
    const service = this.service();
    const data: RemoveHoursData = {
      id: item.Id,
      type: this.hoursType(),
      owner: service instanceof ProductService ? this.owner().JobNumber : (this.owner() as Agreement).AgreementNumber,
      service,
    };

    this.dialog
      .open(RemoveHoursDialog, { data })
      .afterClosed()
      .subscribe({ next: (result) => result && this.removed.emit() });
  }

  showForm() {
    const service = this.service();
    const data: ExtraHoursData = {
      type: this.hoursType(),
      projects: [],
      service,
      owner: service instanceof ProductService ? this.owner().JobNumber : (this.owner() as Agreement).AgreementNumber,
    };

    this.dialog
      .open(ExtraHoursForm, { data })
      .afterClosed()
      .subscribe({ next: (result) => result && this.added.emit() });
  }
}
